import * as polyline from "@mapbox/polyline"
import { Source } from "./mapLibre/Source"
import { Layer, LayerProps } from "./mapLibre/Layer"
import { Feature, Geometry } from "geojson"
import { useMapContext } from "@/components/maps/mapLibre/MapContext"
import { useCallback, useEffect, useRef } from "react"
import { RouteColorScheme, getRouteColorsFromLabel } from "@/lib/routeColors"

// Main line layer - innermost route type color
const alternativeLegLayerFactory = (
  legId: string,
  visible: boolean,
  hoverRouteVisible: boolean,
  colors: RouteColorScheme,
  beforeId?: string,
): LayerProps => ({
  id: `alternative-leg-${legId}`,
  type: "line",
  layout: {
    "line-cap": "butt",
    "line-join": "round",
  },
  paint: {
    "line-color": colors.main,
    "line-opacity": visible ? 1 : 0,
    "line-width": ["interpolate", ["linear"], ["zoom"], 10, 5, 16, 9],
  },
  beforeId,
})

// White middle layer - only visible on hover
const alternativeLegWhiteLayerFactory = (
  legId: string,
  visible: boolean,
  beforeId?: string,
): LayerProps => ({
  id: `alternative-leg-white-${legId}`,
  type: "line",
  layout: {
    "line-cap": "butt",
    "line-join": "round",
  },
  paint: {
    "line-color": "#FFFFFF",
    "line-opacity": [
      "case",
      ["boolean", ["feature-state", "hover"], false],
      visible ? 1 : 0,
      0,
    ],
    "line-width": ["interpolate", ["linear"], ["zoom"], 10, 7, 16, 11],
  },
  beforeId,
})

// Gray outer casing layer - only visible on hover
const alternativeLegCasingLayerFactory = (
  legId: string,
  visible: boolean,
  beforeId?: string,
): LayerProps => ({
  id: `alternative-leg-casing-${legId}`,
  type: "line",
  layout: {
    "line-cap": "butt",
    "line-join": "round",
  },
  paint: {
    "line-color": "#6B7280", // Gray 500
    "line-opacity": [
      "case",
      ["boolean", ["feature-state", "hover"], false],
      visible ? 1 : 0,
      0,
    ],
    "line-width": ["interpolate", ["linear"], ["zoom"], 10, 9, 16, 13],
  },
  beforeId,
})

export type AlternativeLegData = {
  route_handle: string
  polyline: string
}

type AlternativeLegProps = {
  leg: AlternativeLegData
  legIndex: number
  id: string
  beforeId?: string
  onLegClick?: (route_handle: string, legIndex: number) => void
  visible: boolean
  hoverRouteVisible: boolean
  routeLabel?: string // Route label to determine color scheme
}

function AlternativeLeg({
  leg,
  legIndex,
  id,
  onLegClick,
  visible,
  hoverRouteVisible,
  routeLabel,
}: AlternativeLegProps) {
  const mapContext = useMapContext()
  const map = mapContext?.getMapInstance()

  // Refs for tracking state across renders
  const originalLayerBeforeId = useRef<string | undefined>()
  const isHoveredRef = useRef(false)

  // Safety check: don't render if route_handle is invalid
  if (!leg.route_handle || leg.route_handle.trim() === "") {
    return null
  }

  // Determine colors based on route label
  const colors = getRouteColorsFromLabel(routeLabel)

  // Layer IDs as constants to avoid recreation
  const sourceId = `alternative-leg-source-${id}`
  const layerId = `alternative-leg-${id}`
  const whiteLayerId = `alternative-leg-white-${id}`
  const casingLayerId = `alternative-leg-casing-${id}`

  // Memoize the beforeId calculation functions to prevent effect re-runs
  const calculateBeforeId = useCallback((): string | undefined => {
    const layersOrder = map?.getLayersOrder()

    if (!layersOrder || layersOrder.length === 0) {
      return undefined
    }

    for (let i = 0; i < layersOrder.length; i++) {
      if (layersOrder[i].startsWith("maplibre-gl-directions-")) {
        // Return the previous layer if it exists
        return i > 0 ? layersOrder[i - 1] : undefined
      }
    }

    // No directions layer found, return the last layer
    return layersOrder[layersOrder.length - 1]
  }, [map])

  const calculateCasingBeforeId = useCallback(() => {
    const layersOrder = map?.getLayersOrder()
    if (!layersOrder) return undefined

    // Right below the route stack: on top of every base-map layer, below the
    // selected route. The road shields used to be the anchor here, but they
    // are re-anchored above the route lines now, so they no longer mark the
    // top of the base map.
    const firstDirectionsLayer = layersOrder.find((layerId) =>
      layerId.startsWith("maplibre-gl-directions-"),
    )
    if (firstDirectionsLayer) return firstDirectionsLayer

    const mapLayers = map?.getStyle()?.layers
    if (!mapLayers) return undefined

    // Check if highway-shield layer exists
    const highwayShieldLayer = mapLayers.find(
      (layer) => layer.id === "highway-shield",
    )
    if (highwayShieldLayer) return "highway-shield"

    // Check if Railway layer exists
    const railwayLayer = mapLayers.find((layer) => layer.id === "Railway")
    if (railwayLayer) return "Railway"

    return undefined
  }, [map])

  // Create source and layer for this single leg
  const coordinates = polyline
    .decode(leg.polyline)
    .map(([lat, lng]) => [lng, lat])

  const feature: Feature<Geometry, { [p: string]: any }> = {
    type: "Feature",
    id: 0,
    properties: { route_handle: leg.route_handle, legIndex },
    geometry: {
      type: "LineString",
      coordinates,
    },
  }

  const layer = alternativeLegLayerFactory(
    id,
    visible,
    hoverRouteVisible,
    colors,
    calculateBeforeId(),
  )
  const whiteLayer = alternativeLegWhiteLayerFactory(
    id,
    visible,
    calculateBeforeId(),
  )
  const casingLayer = alternativeLegCasingLayerFactory(
    id,
    visible,
    calculateCasingBeforeId(),
  )

  // Event handlers for the layer
  const handleMouseEnter = useCallback(
    (e: any) => {
      if (!map) return

      // Only trigger if this is the topmost layer
      const features = map.queryRenderedFeatures(e.point)
      if (features.length === 0) return

      const topmostFeature = features[0]

      // Don't trigger if a directions layer is on top
      if (topmostFeature.layer.id.startsWith("maplibre-gl-directions")) return
      if (topmostFeature.layer.id !== layerId) return

      // Prevent redundant hover operations
      if (isHoveredRef.current) return

      isHoveredRef.current = true
      map.getCanvas().style.cursor = "pointer"

      // Store the main layer's position before moving to top
      // We only need to track the main layer - casing will be placed right before it
      const mapLayers = map.getStyle()?.layers
      if (mapLayers) {
        const layerIndex = mapLayers.findIndex((l) => l.id === layerId)

        // Store the layer that comes after our main layer (to restore position)
        originalLayerBeforeId.current =
          layerIndex < mapLayers.length - 1
            ? mapLayers[layerIndex + 1].id
            : undefined
      }

      // Move layers to top (no beforeId means they go to the top)
      // Move in order: gray casing, white layer, then main layer
      try {
        map.moveLayer(casingLayerId)
        map.moveLayer(whiteLayerId)
        map.moveLayer(layerId)

        if (map.getSource(sourceId)) {
          map.setFeatureState({ source: sourceId, id: 0 }, { hover: true })
        }
      } catch (err) {
        console.warn("Failed to move layers on hover:", err)
        isHoveredRef.current = false
      }
    },
    [map, sourceId, layerId, whiteLayerId, casingLayerId],
  )

  const handleMouseLeave = useCallback(() => {
    if (!map || !isHoveredRef.current) return

    isHoveredRef.current = false
    map.getCanvas().style.cursor = ""

    // Move layers back to their original positions
    // Restore in reverse order: main layer first, then white, then gray casing
    try {
      // First restore main layer to its original position
      const layerBeforeId = originalLayerBeforeId.current ?? calculateBeforeId()
      if (layerBeforeId) {
        map.moveLayer(layerId, layerBeforeId)
      }

      // Then move white layer immediately before the main layer
      map.moveLayer(whiteLayerId, layerId)

      // Finally move gray casing before the white layer
      map.moveLayer(casingLayerId, whiteLayerId)

      if (map.getSource(sourceId)) {
        map.setFeatureState({ source: sourceId, id: 0 }, { hover: false })
      }
    } catch (err) {
      console.warn("Failed to restore layers on mouse leave:", err)
    }
  }, [map, sourceId, layerId, whiteLayerId, casingLayerId, calculateBeforeId])

  const handleClick = useCallback(
    (e: any) => {
      if (!map) return

      // Only trigger if this is the topmost layer
      const features = map.queryRenderedFeatures(e.point)
      if (features.length === 0) return

      const topmostFeature = features[0]

      // Don't trigger if a directions layer is on top
      if (topmostFeature.layer.id.startsWith("maplibre-gl-directions")) return
      if (topmostFeature.layer.id !== layerId) return

      if (e.features && e.features.length > 0) {
        const feature = e.features[0]
        const route_handle = feature.properties?.route_handle
        const legIdx = feature.properties?.legIndex

        if (route_handle !== undefined && legIdx !== undefined && onLegClick) {
          // Don't clear hover state here - let the component unmount handle cleanup
          // This prevents flicker while the main route is being drawn
          onLegClick(route_handle, legIdx)
        }
      }
    },
    [map, layerId, onLegClick],
  )

  useEffect(() => {
    if (!map) return

    // Use mouseenter/mouseleave instead of mouseover/mouseout
    // mouseenter/mouseleave fire once when entering/leaving the layer
    // mouseover/mouseout fire every time the mouse moves over any feature
    map.on("mouseenter", layerId, handleMouseEnter)
    map.on("mouseleave", layerId, handleMouseLeave)
    map.on("click", layerId, handleClick)

    return () => {
      map.off("mouseenter", layerId, handleMouseEnter)
      map.off("mouseleave", layerId, handleMouseLeave)
      map.off("click", layerId, handleClick)

      // Clean up hover state if currently hovered
      if (isHoveredRef.current) {
        isHoveredRef.current = false

        try {
          if (
            map.getLayer(layerId) &&
            map.getLayer(whiteLayerId) &&
            map.getLayer(casingLayerId)
          ) {
            // First restore main layer to its original position
            const layerBeforeId =
              originalLayerBeforeId.current ?? calculateBeforeId()
            if (layerBeforeId) {
              map.moveLayer(layerId, layerBeforeId)
            }

            // Then move white layer immediately before the main layer
            map.moveLayer(whiteLayerId, layerId)

            // Finally move gray casing before the white layer
            map.moveLayer(casingLayerId, whiteLayerId)
          }

          if (map.getSource(sourceId)) {
            map.setFeatureState({ source: sourceId, id: 0 }, { hover: false })
          }

          map.getCanvas().style.cursor = ""
        } catch (err) {
          console.warn("Failed to cleanup hover state on unmount:", err)
        }
      }
    }
  }, [
    map,
    sourceId,
    layerId,
    whiteLayerId,
    casingLayerId,
    handleMouseEnter,
    handleMouseLeave,
    handleClick,
    calculateBeforeId,
  ])

  return (
    <Source id={sourceId} type="geojson" data={feature}>
      <Layer {...casingLayer} />
      <Layer {...whiteLayer} />
      <Layer {...layer} />
    </Source>
  )
}

export default AlternativeLeg
